import ChartLoading from '@/components/common/charts/ChartLoading';
import { FlexRow } from '@/components/common/Flex';
import DashboardLineChart, {
    CHART_CONTAINER_STYLES,
    CHART_MARGIN,
} from '@/components/dashboard/DashboardLineChart';
import { useQuery } from '@apollo/client';
import { Box } from '@chakra-ui/react';
import {
    AnyFinancialEntity,
    isPlentyGoalAccount,
} from '@common/financialEntities';
import { NetWorthValueSeriesDocument } from '@common/graphql/generated';
import {
    minDateFromFEs,
    useFinancialEntitiesContext,
    useHasNoFEs,
} from '@common/react/financialEntities';
import { usePageContext } from '@common/react/pageMode';
import { ageOnDate, useCurrentUser } from '@common/react/user';
import styleguide from '@common/styleguide';
import { toDollars } from '@common/util/currency';
import { dt } from '@common/util/dateTime';
import { NetWorthValueNode, TickRange, ValueSeries } from '@common/valueSeries';
import { ParentSize } from '@visx/responsive';
import { useMemo } from 'react';
import {
    ChartDateRangeLabel,
    ChartRangeControlsInput,
    ChartRangeSelector,
    ChartRangeStepperButton,
    useChartRangeControls,
} from '../common/charts/ChartRangeControls';
import {
    ChartTooltipRow,
    ChartTooltipSummaryRow,
} from '../common/charts/ChartTooltip';
import ErrorBoundary from '../common/ErrorBoundary';
import FadeInBox from '../common/FadeInBox';
import { Text } from '../common/Typography';
import DashboardNoFEsChart from './DashboardNoFEsChart';

const DashboardNetWorthChart = () => {
    const { fes } = useFinancialEntitiesContext();
    const hasNoFEs = useHasNoFEs();

    if (hasNoFEs) {
        return (
            <FlexRow
                key="dashboard-chart-loading"
                pos="relative"
                {...CHART_CONTAINER_STYLES}
            >
                <FlexRow grow={1} position="relative" maxH="full">
                    <ParentSize>
                        {(parent) => (
                            <Box opacity={0.5}>
                                <DashboardNoFEsChart
                                    width={parent.width}
                                    height={parent.height}
                                    stroke={[styleguide.colors.greenBright]}
                                />
                            </Box>
                        )}
                    </ParentSize>
                </FlexRow>
            </FlexRow>
        );
    }

    if (fes.loading || !fes.data) {
        return (
            <FlexRow
                id=""
                key="dashboard-chart-loading"
                pos="relative"
                {...CHART_CONTAINER_STYLES}
            >
                <ChartLoading />
            </FlexRow>
        );
    }

    return (
        <FadeInBox
            key="dashboard-chart-loaded"
            {...CHART_CONTAINER_STYLES}
            w="full"
            h="full"
        >
            <DashboardNetWorthInnerChart
                financialEntities={fes.data.financialEntities}
            />
        </FadeInBox>
    );
};

interface DashboardNetWorthChartInnerProps {
    financialEntities: AnyFinancialEntity[];
}

const DashboardNetWorthInnerChart = ({
    financialEntities,
}: DashboardNetWorthChartInnerProps) => {
    const { userInfo } = useCurrentUser();
    const hasFundedGoals =
        !!financialEntities.filter(isPlentyGoalAccount).length;

    const { minDate, maxDate } = useMemo(() => {
        const minDate = minDateFromFEs(financialEntities);
        const maxDate = userInfo.finalDate;
        return { minDate, maxDate };
    }, [financialEntities, userInfo.finalDate]);

    const defaultChartRangeInput: ChartRangeControlsInput = hasFundedGoals
        ? {
              initialType: '1Y',
              minDate,
              maxDate,
              key: 'dashboard',
          }
        : {
              initialStartDate: dt().toDateStr(),
              initialType: '5Y',
              minDate,
              maxDate,
              key: 'dashboard',
          };

    const { chartRangeType, range, onIncrement, onDecrement, onChangeType } =
        useChartRangeControls(defaultChartRangeInput);

    const { data: valueSeriesData, loading: valueSeriesDataLoading } = useQuery(
        NetWorthValueSeriesDocument,
        {
            variables: {
                financialEntityIds: financialEntities.map((fe) => fe.id),
                range,
                currentDate: dt().toDateStr(),
            },
        }
    );

    if (!valueSeriesData || valueSeriesDataLoading) {
        return (
            <FlexRow
                id=""
                key="dashboard-chart-loading"
                pos="relative"
                {...CHART_CONTAINER_STYLES}
            >
                <ChartLoading />
            </FlexRow>
        );
    }

    return (
        <FlexRow grow={1} position="relative" maxH="full">
            <ChartDateRangeLabel range={range} />
            <ChartRangeSelector
                top={2}
                type={chartRangeType}
                onChange={onChangeType}
            />
            <ChartRangeStepperButton
                margin={CHART_MARGIN}
                type="decrement"
                onClick={onDecrement}
            />
            <ChartRangeStepperButton
                margin={CHART_MARGIN}
                type="increment"
                onClick={onIncrement}
            />

            <ParentSize>
                {(parent) => (
                    <ErrorBoundary>
                        <NetWorthChart
                            width={parent.width}
                            height={parent.height}
                            range={range}
                            valueSeries={
                                valueSeriesData.netWorthValueSeries as ValueSeries<NetWorthValueNode>
                            }
                            financialEntities={financialEntities}
                        />
                    </ErrorBoundary>
                )}
            </ParentSize>
        </FlexRow>
    );
};

const NetWorthChart = ({
    range,
    width,
    height,
    valueSeries,
}: {
    range: TickRange;
    width: number;
    height: number;
    financialEntities: AnyFinancialEntity[];
    valueSeries: ValueSeries<NetWorthValueNode>;
}) => {
    const { userInfo } = useCurrentUser();
    const { pageMode } = usePageContext();
    const color = pageMode === 'MINE' ? '#6E7FB7' : '#6EB77A';

    return (
        <DashboardLineChart
            range={range}
            width={width}
            height={height}
            valueSeries={valueSeries}
            yValueGetter={(d) => d?.netWorthTotal ?? null}
            color={color}
            tooltipProps={{
                renderTitle: (d) =>
                    dt(d.date).format(
                        range.unit === 'day' ? 'ddd, MMM D, YYYY' : 'MMM, YYYY'
                    ),
                renderBody: (d) => (
                    <>
                        {d.values.map((v) => {
                            return (
                                <ChartTooltipRow
                                    key={`tooltip-row-${v.label}-${d.date}-${v.total}`}
                                    label={v.label}
                                    value={toDollars(v.total)}
                                />
                            );
                        })}
                        <ChartTooltipSummaryRow
                            key={`tooltip-summary-row-${d.date}`}
                            label="TOTAL"
                            value={toDollars(d.netWorthTotal)}
                        />
                    </>
                ),
                renderHeaderRight: (d) =>
                    userInfo.dob ? (
                        <Text
                            key="tooltip-header-right"
                            color="gray"
                            fontSize="sm"
                        >
                            {'Age '}
                            {ageOnDate(userInfo.dob, d.date)}
                        </Text>
                    ) : (
                        <></>
                    ),
            }}
        />
    );
};

export default DashboardNetWorthChart;
